/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import isEmpty from 'lodash/isEmpty';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FormattedMessage, IntlProvider } from 'react-intl';
import { QueryClientProvider } from 'react-query';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import styled, { ThemeProvider } from 'styled-components';
import { Button } from 'components/atoms/button';
import { Form } from 'components/atoms/form';
import { Input } from 'components/atoms/input';
import { Modal } from 'components/atoms/modal';
import { OutsideClickHandler } from 'components/atoms/outside-click-handler';
import { Tag } from 'components/atoms/tag';
import { SmartFillTagColors } from 'components/colors';
import { theme } from 'components/styles/theme';
import { store } from 'data';
import { TranslationKey, useTranslate } from 'hooks/use-translation';
import { queryClient } from 'queries';
import translations from 'translations/en.json';
import { getRandomColor } from 'utils/get-random-color';
import { isChildNode } from 'utils/is-child-node';
import {
  SimpleVariablesPopover,
  StyledVariablesPopover,
} from './simple-variables-select';
import { useGetFieldsForVariables } from './use-get-fields-for-variables';

const StyledOutsideClickHandler = styled(OutsideClickHandler)`
  display: inline;
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const ModalPopover = styled(StyledVariablesPopover)<{
  left: number;
  top: number;
}>`
  &.ant-popover {
    height: initial;
  }
  left: ${(props) => `${props.left}px !important`};
  top: ${(props) => `${props.top}px !important`};
`;

const SimpleVariableTag = styled(Tag)<{
  active: boolean;
}>`
  display: inline-flex;
  align-items: center;
  color: #e5e5e5;
  border: ${(props) =>
    props.active
      ? '2px solid var(--primary-color)'
      : '1px solid var(--gray-20)'};
  gap: var(--spacing-02);
  padding: var(--spacing-01) var(--spacing-02);

  &:hover {
    border: ${(props) =>
      props.active
        ? '2px solid var(--primary-color)'
        : '1px solid var(--gray-30)'};
  }
`;

const FallbackModal = ({ wrapperRef, initialValue, onDone, onClose }) => {
  const [form] = Form.useForm();
  const _onDone = useCallback(async () => {
    const formValues = await form.validateFields();

    onDone(formValues.fallback);
  }, [form, onDone]);

  return (
    <Modal
      wrapClassName="fallback-modal-wrap"
      getContainer={() => wrapperRef?.current}
      visible
      closable={false}
      width={316}
      footer={null}
      onCancel={onClose}
      mask={false}
      transitionName=""
    >
      <Form
        autoComplete="off"
        layout="vertical"
        form={form}
        initialValues={{
          fallback: initialValue,
        }}
      >
        <Form.Item
          label={<FormattedMessage id="variables.simple.fallback.label" />}
          name="fallback"
        >
          <Input />
        </Form.Item>
        <Form.Item noStyle>
          <ModalFooter>
            <Button
              size="small"
              onClick={_onDone}
              type="link"
              htmlType="submit"
            >
              <FormattedMessage id="done" />
            </Button>
          </ModalFooter>
        </Form.Item>
      </Form>
    </Modal>
  );
};

/**
 * strips fieldId suffixed to field name.
 */
const formatFieldName = (name: string) => name.replace(/_\d*$/, '');

const SimpleVariableText = ({
  fieldName: intialFieldName,
  fallback: initialFallback,
  editor,
  modelItem,
  writer,
  templateType,
  meta,
}) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [active, setActive] = useState(false);
  const [visible, setVisible] = useState(false);
  const [editFallback, setEditFallback] = useState(false);
  const [fieldName, setFieldName] = useState(formatFieldName(intialFieldName));
  const [fallback, setFallback] = useState(initialFallback);
  const [coords, setCoords] = useState({ left: 0, top: 0 });
  const { fields, nestedFields } = useGetFieldsForVariables(
    templateType,
    editor,
  );
  const translate = useTranslate();

  useEffect(() => {
    setActive(visible || editFallback);
  }, [visible, editFallback]);

  const onSelect = useCallback(
    ({ fieldName: _name, fieldNameForBackend, objectType }) => {
      const newFieldName = `${
        objectType
          ? `${translate(`source_type.${objectType}` as TranslationKey)} → `
          : ''
      }${_name}`;

      setVisible(false);
      setFieldName(formatFieldName(newFieldName));
      writer.setAttribute('fieldName', newFieldName, modelItem);
      writer.setAttribute(
        'fieldNameForBackend',
        fieldNameForBackend,
        modelItem,
      );

      writer.setAttribute('fields', fields, modelItem);
      writer.setAttribute('nestedFields', nestedFields, modelItem);
      writer.setAttribute('templateType', templateType, modelItem);
      // TODO: add add keys using Object.keys(meta)
      writer.setAttribute('type', meta?.type, modelItem);
      writer.setAttribute(
        'triggerIdentifier',
        meta?.triggerIdentifier,
        modelItem,
      );
      editor.setData(editor.getData());
    },
    [
      editor,
      fields,
      meta?.triggerIdentifier,
      meta?.type,
      modelItem,
      nestedFields,
      templateType,
      translate,
      writer,
    ],
  );

  const onFallbackDone = useCallback(
    (_fallbackValue) => {
      setEditFallback(false);
      setFallback(_fallbackValue);

      writer.setAttribute('fallback', _fallbackValue, modelItem);
      editor.setData(editor.getData());

      editor.editing.view.focus();
    },
    [editor, modelItem, writer],
  );

  const isChild = isChildNode(['ck-variables-popup']);

  const setCoordsFn = useCallback(
    (event: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
      const startX = event.currentTarget.getBoundingClientRect().left;
      const initialX = startX + 316; // 316px is modal width
      const offsetX = initialX - window.innerWidth;
      const targetX = offsetX < 0 ? startX : startX - offsetX - 16; // 16px for padding;

      setCoords({
        left: targetX,
        top: event.clientY + 20,
      });
    },
    [],
  );

  return (
    <StyledOutsideClickHandler
      dataCy={`smartfill.tag.field.${fieldName}`}
      className="simple-variables-as-tags"
      allowed={isChild}
      onOutsideClick={() => {
        setVisible(false);
        setEditFallback(false);
      }}
    >
      {fieldName ? (
        <SimpleVariableTag active={active}>
          <Tag
            data-cy={`smartfill.tag.field.content.${fieldName}`}
            onClick={() => {
              if (editor.isReadOnly) {
                return;
              }
              setEditFallback(false);
              setVisible(true);
            }}
            color={getRandomColor(fieldName, SmartFillTagColors)}
          >
            {fieldName}
          </Tag>
          <span>|</span>
          {!isEmpty(fallback) ? (
            <Tag
              data-cy={`smartfill.tag.field.fallback.${fallback}`}
              onClick={(event) => {
                if (editor.isReadOnly) {
                  return;
                }
                setVisible(false);
                setCoordsFn(event);
                setEditFallback(true);
              }}
              color="blue"
            >
              {fallback}
            </Tag>
          ) : (
            <Tag
              data-cy="smartfill.tag.field.fallback.add_fallback"
              onClick={(event) => {
                if (editor.isReadOnly) {
                  return;
                }
                setVisible(false);
                setCoordsFn(event);
                setEditFallback(true);
              }}
              color="gray"
            >
              <FormattedMessage id="variables.simple.add.fallback" />
            </Tag>
          )}
        </SimpleVariableTag>
      ) : null}
      <SimpleVariablesPopover
        visible={visible}
        onSelect={onSelect}
        fields={fields}
        nestedFields={nestedFields}
        templateType={templateType}
      />
      <ModalPopover
        className="ck-variables-popup"
        arrowContent={false}
        visible={editFallback}
        left={coords.left}
        top={coords.top}
        content={
          <div ref={ref}>
            <FallbackModal
              wrapperRef={ref}
              initialValue={fallback}
              onDone={onFallbackDone}
              onClose={() => setEditFallback(false)}
            />
          </div>
        }
      />
    </StyledOutsideClickHandler>
  );
};

export const SimpleVariablesAsTags = ({
  fieldName,
  fallback,
  editor,
  modelItem,
  writer,
  templateType,
  meta,
}) => {
  return (
    <Provider store={store}>
      <BrowserRouter>
        <ThemeProvider theme={theme}>
          <IntlProvider locale="en" messages={translations}>
            <QueryClientProvider client={queryClient}>
              {editor.config._config?.variablesConfig
                ?.resolveVariablesToFallback ? (
                <>{fallback ?? ''}</>
              ) : (
                <SimpleVariableText
                  fieldName={fieldName}
                  fallback={fallback}
                  editor={editor}
                  modelItem={modelItem}
                  writer={writer}
                  meta={meta}
                  templateType={templateType}
                />
              )}
            </QueryClientProvider>
          </IntlProvider>
        </ThemeProvider>
      </BrowserRouter>
    </Provider>
  );
};
